<template>
  <div class="relative flex w-full" style="min-height: 2.5rem" :style="inputStyles">
    <div
      v-if="required && !requiredIconHidden"
      v-tooltip="'Required field'"
      class="absolute right-0 pt-0.5 pr-1.5 text-lg text-error"
    >
      *
    </div>
    <div v-if="readonly && !tooltip && !required"
      v-tooltip="'Read-only field'"
      class="absolute right-0 top-1/2 -translate-y-1/2 mr-1.5 text-neutral-content/50 w-4 h-4"
    >
      <LockClosedIcon />
    </div>
    <div v-if="tooltip">
      <InformationCircleIcon
        class="absolute right-0 bottom-0 mb-1 mr-1 text-info w-4 h-4"
        v-tooltip="tooltip"
      />
    </div>
    <input
      :id="id || label"
      :type="type"
      ref="input"
      :required="required"
      class="ai-input-class ai-input-dynamic-ring peer"
      :class="[
        error ? 'ai-input-class-error' : '',
        readonly ? 'ai-input-readonly' : '',
        internalBgColor,
        inputClass
      ]"
      :placeholder="placeholder"
      :aria-describedby="label"
      v-model="internalValue"
      :disabled="disabled"
      :readonly="readonly"
      :minlength="minLength"
      :autocomplete="autocomplete"
    />
    <label :for="id || label" class="ai-input-label peer-focus:font-normal" :class="internalBgColor">
      {{ label }}
    </label>
  </div>
</template>

<script setup>
import { computed, inject } from "vue"
import { InformationCircleIcon, LockClosedIcon } from "@heroicons/vue/24/outline"
import { useToggleThemeStore } from "@/stores/toggleTheme"
import { colorNameToBgClass, colorNameToCssVar } from "@/composables/useColors"

const themeStore = useToggleThemeStore()
const formSettings = inject('form') ?? {}


const props = defineProps({
  id: {
    type: String,
  },
  modelValue: [String, Number],
  label: {
    type: String,
  },
  placeholder: {
    type: String,
    default: " ",
  },
  type: {
    type: String,
    default: "text",
  },
  required: {
    type: Boolean,
    default: false,
  },
  requiredIconHidden: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  inputClass: {
    type: String,
    default: "",
  },
  minLength: {
    type: Number,
  },
  error: {
    type: Boolean,
    default: false,
  },
  bgColor: {
    type: String,
    default: '',
  },
  borderColor: {
    type: String,
    default: '',
    // Note: For XInput, this controls ring color, not actual border
  },
  borderColorFocus: {
    type: String,
    default: '',
    // Note: For XInput, this controls focus ring color
  },
  borderColorHover: {
    type: String,
    default: '',
    // Note: For XInput, this controls hover ring color
  },
  labelColorFocus: {
    type: String,
    default: '',
  },
  autocomplete: {
    type: String,
    default: '',
  },
})


const internalBgColor = computed(() => {
  const colorName = props.bgColor || formSettings.bgColor || 'base-100'
  return colorNameToBgClass(colorName)
})

const internalRingColor = computed(() => props.borderColor || formSettings.borderColor || 'form-border')
const internalRingColorFocus = computed(() => props.borderColorFocus || formSettings.borderColorFocus || 'form-border-focus')
const internalRingColorHover = computed(() => props.borderColorHover || formSettings.borderColorHover || 'form-border-hover')
const internalLabelColorFocus = computed(() => props.labelColorFocus || formSettings.labelColorFocus || 'base-content')

// Computed styles for dynamic ring and label colors
const inputStyles = computed(() => ({
  '--ai-input-ring': colorNameToCssVar(internalRingColor.value),
  '--ai-input-ring-focus': colorNameToCssVar(internalRingColorFocus.value),
  '--ai-input-ring-hover': colorNameToCssVar(internalRingColorHover.value),
  '--ai-input-label-focus': colorNameToCssVar(internalLabelColorFocus.value)
}))

const emit = defineEmits(["update:modelValue"])

const internalValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    emit("update:modelValue", newValue)
  },
})
</script>

<style scoped>
.ai-input-dynamic-ring {
  --tw-ring-color: var(--ai-input-ring);
  transition: box-shadow 200ms;
}

.ai-input-dynamic-ring:focus {
  --tw-ring-color: var(--ai-input-ring-focus);
}

.ai-input-dynamic-ring:hover:not(:focus) {
  --tw-ring-color: var(--ai-input-ring-hover);
}
</style>
